import { mergeUpsertOffline } from "utils/offline/crud"
import { RESOURCE_TO_STORE } from "utils/offline/map"
import { writeData, readAllData, deleteItemFromData, STORES } from "utility"
import { AUTH_TOKEN } from "constants/AuthConstant"
import axiosInstance from "utils/axiosInstance"

export async function enqueueRequest(request) {
  await writeData(STORES.OUTBOX, request)
}

export async function getQueue() {
  return await readAllData(STORES.OUTBOX)
}

export async function removeFromQueue(id) {
  await deleteItemFromData(STORES.OUTBOX, id)
}

export async function hasPendingRequests() {
  const queue = await getQueue()
  return queue.length > 0
}

function parsePath(path) {
  const [resource, action] = path.split("/")
  return { resource, action }
}

// ──────────────────────────────────────────────────────────────────────────
// Map persistante tempUUID → id serveur réel.
// L'ancien remapping (updateRemainingPaths) ne fonctionnait qu'au sein d'un
// même run de sync et seulement si le create précédait l'édition. Cette map
// persiste dans localStorage et est appliquée à CHAQUE requête juste avant
// l'envoi — elle résout donc aussi les éditions synchronisées dans un autre
// batch, ou enfilées par des mutations custom (ex: waitingList/{id}/status).
// ──────────────────────────────────────────────────────────────────────────
const ID_MAP_KEY = "OFFLINE_ID_MAP"

// Champs FK entiers en base : un UUID y est invalide, il faut l'id réel (Number).
const INTEGER_FK_FIELDS = new Set(["appointmentId"])
// Champs FK qui acceptent une string (id réel string).
const STRING_FK_FIELDS = new Set([
  "patientId",
  "consultationId",
  "waitingListId",
])

function getIdMap() {
  try {
    return JSON.parse(localStorage.getItem(ID_MAP_KEY) || "{}")
  } catch {
    return {}
  }
}

function rememberId(tempId, realId) {
  if (!tempId || !realId || String(tempId) === String(realId)) return
  try {
    const map = getIdMap()
    map[String(tempId)] = String(realId)
    localStorage.setItem(ID_MAP_KEY, JSON.stringify(map))
  } catch {
    /* localStorage indisponible : on ignore */
  }
}

// Réécrit le path et les FK du body d'une requête à partir de la map persistante.
function resolveTempIds(req) {
  const map = getIdMap()
  if (!Object.keys(map).length) return req

  if (req.path) {
    for (const [tempId, realId] of Object.entries(map)) {
      if (req.path.includes(tempId)) {
        req.path = req.path.split(tempId).join(realId)
      }
    }
  }

  if (req.body && typeof req.body === "object") {
    for (const field of STRING_FK_FIELDS) {
      const val = req.body[field]
      if (val != null && map[String(val)]) {
        req.body[field] = map[String(val)]
      }
    }
    for (const field of INTEGER_FK_FIELDS) {
      const val = req.body[field]
      if (val != null && map[String(val)]) {
        req.body[field] = Number(map[String(val)])
      }
    }
  }

  return req
}

/**
 * Map clé de réponse → store. Utilisée pour extraire 1 ou plusieurs entités
 * du payload serveur (ex: addPayment renvoie consultation + payment).
 */
const RESPONSE_ENTITY_MAP = {
  patient: STORES.PATIENTS,
  consultation: STORES.CONSULTATIONS,
  appointment: STORES.APPOINTMENTS,
  payment: STORES.PAYMENTS,
  medicine: STORES.MEDICINE,
  prescription: STORES.PRESCRIPTIONS,
  bilan: STORES.BILANS,
  workStoppage: STORES.ARRETS_TRAVAIL,
  medicalCertificate: STORES.CERTIFICATS,
  orientationLetter: STORES.LETTRES_ORIENTATION,
  justification: STORES.JUSTIFICATIONS,
  waitingList: STORES.WAITING_LIST,
  prestation: STORES.PRESTATIONS,
  reminder: STORES.REMINDERS,
  user: STORES.USERS,
  expenses: STORES.EXPENSES,
}

function extractServerEntities(response, fallbackStore = null) {
  const root = response?.data?.data ?? response?.data ?? response
  if (!root || typeof root !== "object") return []

  const entities = []
  const rootHasId = root.id || root._id

  if (rootHasId) {
    // Le root EST l'entité créée/modifiée (ex: waitingList, appointment...),
    // potentiellement avec des relations imbriquées (patient, appointment...).
    // On la cache entière sous son propre store. ⚠️ Ne PAS traiter ses relations
    // imbriquées comme des entités de premier niveau : leurs clés (patient,
    // appointment) matchent RESPONSE_ENTITY_MAP et feraient prendre le mauvais
    // id comme "realId" lors du remapping (bug "Waiting entry not found").
    if (fallbackStore) {
      entities.push({ store: fallbackStore, data: root })
    }
  } else {
    // Réponse à clé(s) explicite(s) sans id racine
    // (ex: addPayment → { consultation: {...}, payment: {...} }).
    for (const [key, store] of Object.entries(RESPONSE_ENTITY_MAP)) {
      const candidate = root?.[key]
      if (
        candidate &&
        typeof candidate === "object" &&
        (candidate.id || candidate._id)
      ) {
        entities.push({ store, data: candidate })
      }
    }

    // "updated" générique : on rabat sur le store du resource
    if (root?.updated && fallbackStore) {
      const u = root.updated
      if (u && (u.id || u._id)) {
        entities.push({ store: fallbackStore, data: u })
      }
    }
  }

  return entities
}

async function updateRemainingPaths(tempId, realId, resource, queue) {
  // ✅ Bug 12 fix: on mute le tableau in-memory (pas de second getQueue)
  for (const req of queue) {
    let hasChanged = false
    let newPath = req.path
    let newBody = req.body ? { ...req.body } : undefined

    if (newPath.includes(tempId)) {
      newPath = newPath.split(tempId).join(realId)
      hasChanged = true
    }

    if (newBody) {
      if (resource === "patient" && newBody.patientId === tempId) {
        newBody.patientId = realId
        hasChanged = true
      }
      if (resource === "consultation" && newBody.consultationId === tempId) {
        newBody.consultationId = realId
        hasChanged = true
      }
      if (resource === "appointment" && newBody.appointmentId === tempId) {
        // appointmentId is Int in the schema — Joi rejects a string UUID
        newBody.appointmentId = Number(realId)
        hasChanged = true
      }
    }

    if (hasChanged) {
      req.path = newPath
      req.body = newBody
      await writeData(STORES.OUTBOX, req)
    }
  }
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// FK fields that are nullable integers in the DB schema.
// If a UUID ends up here with no prior remapping, null is the safe fallback
// (Joi accepts null, the row is created without the broken FK link).
const NULLABLE_INTEGER_FK_FIELDS = new Set(["appointmentId"])

function resolveNullableUUIDs(body) {
  if (!body) return body
  const out = { ...body }
  for (const [key, val] of Object.entries(out)) {
    if (
      typeof val === "string" &&
      UUID_RE.test(val) &&
      NULLABLE_INTEGER_FK_FIELDS.has(key)
    ) {
      out[key] = null
    }
  }
  return out
}

export async function syncOfflineQueue() {
  // ✅ Bug 12 fix: une seule lecture
  const queue = (await getQueue()).sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
  )

  const skippedErrors = []

  for (const req of queue) {
    try {
      // Résout les tempUUID → id serveur réel (path + FK body) avant l'envoi.
      // Couvre les éditions enfilées avant que le create correspondant n'ait
      // été synchronisé, et les cas cross-batch.
      resolveTempIds(req)

      const { resource } = parsePath(req.path)
      const token = localStorage.getItem(AUTH_TOKEN)
      // ✅ Bug 8 fix: plus de log du token

      // ✅ Bug 11 fix: __pendingSync supprimé du destructuring
      const cleanBody = resolveNullableUUIDs(
        req.body
          ? (() => {
              // eslint-disable-next-line no-unused-vars
              const { id, __offline, createdAt, updatedAt, ...rest } = req.body
              return rest
            })()
          : undefined,
      )

      const res = await axiosInstance({
        method: req.method,
        url: req.path,
        data:
          req.method !== "get" && req.method !== "delete"
            ? cleanBody
            : undefined,
        headers: {
          Authorization: `Bearer ${token}`,
          "Idempotency-Key": req.id,
          "X-Offline-Sync": "true",
        },
      })

      if (res.status >= 400) {
        throw new Error(`Erreur serveur: ${res.status}`)
      }

      // ============ MISE À JOUR DU CACHE ============
      // ✅ Bug 2 fix: on ne dépend plus du flag __offline dans body
      if (req.method.toLowerCase() !== "delete") {
        const fallbackStore = RESOURCE_TO_STORE[resource]
          ? STORES[RESOURCE_TO_STORE[resource]]
          : null

        const serverEntities = extractServerEntities(res, fallbackStore)
        const tempIdFromBody = req.body?.id
        const isCreate = req.body?.__offline === true && tempIdFromBody

        for (const { store, data } of serverEntities) {
          let merged = data
          // Cleanup tempId pour les CREATE (id serveur ≠ tempId UUID)
          // Avant suppression, on récupère les champs locaux pour les préserver :
          // la réponse serveur addPayment/update peut être partielle et écraserait
          // motif / patientId / montant / etc. sans cette fusion.
          if (
            isCreate &&
            tempIdFromBody &&
            String(data.id) !== String(tempIdFromBody)
          ) {
            try {
              const all = await readAllData(store)
              const localAtTempId = all.find(
                (item) => String(item.id) === String(tempIdFromBody),
              )
              if (localAtTempId) {
                const { __offline, id: _oldId, ...localRest } = localAtTempId
                merged = { ...localRest, ...data, id: data.id }
              }
              await deleteItemFromData(store, String(tempIdFromBody))
            } catch {
              /* ignore */
            }
          }
          // mergeUpsertOffline retire __offline et préserve les champs locaux
          // absents de la réponse serveur.
          await mergeUpsertOffline(store, merged, { markOffline: false })
        }

        // Cleanup metadata : tempIds qui ne sont pas dans body.id
        // (ex: addPayment crée un payment.id en plus du body montant/payment/...)
        if (Array.isArray(req.__cleanup)) {
          for (const { store, id } of req.__cleanup) {
            try {
              await deleteItemFromData(store, id)
            } catch {
              /* ignore */
            }
          }
        }

        // Remapping des IDs pour les requêtes suivantes (POST/create uniquement)
        // On prend l'entité du store de la ressource créée (pas serverEntities[0]
        // qui pourrait être une relation imbriquée → mauvais id).
        const primary =
          serverEntities.find((e) => e.store === fallbackStore) ||
          serverEntities[0]
        const realId = primary?.data?.id || primary?.data?._id
        if (
          req.method.toLowerCase() === "post" &&
          isCreate &&
          realId &&
          tempIdFromBody &&
          String(realId) !== String(tempIdFromBody)
        ) {
          // Persiste le mapping (survit aux runs/batches) + remappe le batch courant.
          rememberId(String(tempIdFromBody), String(realId))
          await updateRemainingPaths(
            String(tempIdFromBody),
            String(realId),
            resource,
            queue,
          )
        }
      }

      await removeFromQueue(req.id)
    } catch (e) {
      const status = e?.response?.status
      const serverMessage =
        e?.response?.data?.error ||
        e?.response?.data?.errors?.join(", ") ||
        e.message
      if (status && status >= 400 && status < 500) {
        // Validation error — this request can never succeed, discard it
        await removeFromQueue(req.id)
        const { resource, action } = parsePath(req.path)
        skippedErrors.push(`${resource}/${action}: ${serverMessage}`)
        continue
      }
      // Network or server error — stop and let user retry
      return {
        success: false,
        error: serverMessage || "Erreur de synchronisation",
        skippedErrors,
      }
    }
  }

  // File vidée avec succès : plus aucune requête ne référence de tempUUID,
  // on purge la map persistante pour éviter qu'elle ne grossisse indéfiniment.
  try {
    if (!(await hasPendingRequests())) {
      localStorage.removeItem(ID_MAP_KEY)
    }
  } catch {
    /* ignore */
  }

  return { success: true, skippedErrors }
}

export function buildFullName(patient) {
  if (!patient) return ""
  return `${patient.nom || ""} ${patient.prenom || ""}`.trim().toLowerCase()
}
